import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { LprIntegrationType } from '@lib/data-dog-rum/custom-action-map';

import {
  FsLprAxonVIRead,
  FsLprAxonVIResults,
  FsLprAxonVIUser,
  FsLprFiltersListResponse,
  IFsLprAxonVIParameters,
  FsLprAxonVIFilterSuggestion,
  FsLprAxonVIFilterSuggestionResults,
} from '../api';
import {
  extractLprErrorCode,
  extractLprRawError,
  parseLprApiError,
} from '../utils/lpr-error-utils';

@Injectable({
  providedIn: 'root',
})
export class AxonVILprService {
  private readonly _integrationType: LprIntegrationType = 'axon_vi';

  // API endpoints
  private readonly _primaryEndpoint = '/api/integrations/axon/axon_vi/search/';

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _metrics: FsDataDogRumService,
    private readonly _toastrService: ToastrService,
  ) {}

  /**
   * Fetches license plate data from the Vehicle Intelligence API
   *
   * @param params Query parameters for the search (size, page, search_reason, etc.)
   * @returns Observable of the search results with metadata
   */
  public getAxonVIPlates(params: Partial<IFsLprAxonVIParameters>): Observable<FsLprAxonVIResults> {
    this.processGetAxonVIPlatesParams(params);

    const httpParams = new HttpParams({ fromObject: { ...params } });
    const startTime = Date.now();

    return this._httpClient
      .get<FsLprAxonVIResults>(this._primaryEndpoint, {
        params: httpParams,
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const data = response.body;
          if (Array.isArray(data.data)) {
            data.data = data.data.map((result) => new FsLprAxonVIRead(result));
          }

          this._metrics.addAction('lpr_search', {
            status: response.status,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
          });

          return {
            ...data,
            status: response.status,
          };
        }),
        catchError((primaryError) => {
          // Log the primary endpoint error
          this._metrics.addAction('lpr_search_error', {
            status: primaryError?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
            raw_error: extractLprRawError(primaryError),
            error_code: extractLprErrorCode(primaryError),
            error_message: parseLprApiError(primaryError),
          });

          // Fall back to the legacy endpoint
          return throwError(() => primaryError);
        }),
      );
  }

  /**
   * Retrieves a specific LPR read from the Axon VI integration.
   *
   * @param {Object} params - The parameters for the read request
   * @param {string} params.readId - The readId to retrieve
   * @param {number} params.timestamp - The timestamp of search
   * @param {string} params.searchReason - The reason for the search
   * @returns {Observable<FsLprAxonVIRead>} Observable that emits the LPR read
   */
  public getRead({
    readId,
    timestamp,
    searchReason,
  }: {
    readId: string;
    timestamp: number;
    searchReason: string;
  }): Observable<FsLprAxonVIRead> {
    return this._httpClient
      .get(`${this._primaryEndpoint}${readId}/`, {
        params: {
          timestampMillis: timestamp,
          searchReason,
        },
      })
      .pipe(
        map((response) => {
          return new FsLprAxonVIRead(response);
        }),
        catchError((error) => {
          console.error('Failed to fetch read:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Retrieves available filters for LPR searches from the Axon VI integration.
   *
   * @returns {Observable<FsLprFiltersListResponse>} Observable that emits the available record search filters
   */
  public getFilters(): Observable<FsLprFiltersListResponse> {
    // TODO: Replace with the actual filters endpoint when it is made available: https://taserintl.atlassian.net/browse/FWX-2449
    return this._httpClient.get('/api/integrations/axon/axon_vi/filters/').pipe(
      map((response) => {
        return new FsLprFiltersListResponse(response);
      }),
    );
  }

  public getCameraNameSuggestions(searchTerm: string): Observable<FsLprAxonVIFilterSuggestion[]> {
    return this._httpClient
      .get<FsLprAxonVIFilterSuggestionResults>(`${this._primaryEndpoint}filters/suggestions/`, {
        params: {
          fieldName: 'camera.name',
          searchTerm,
          limit: 100,
        },
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const data = response.body?.data;
          if (Array.isArray(data)) {
            return data.map((item) => new FsLprAxonVIFilterSuggestion(item));
          }
          return [];
        }),
        catchError((error) => {
          console.error('Failed to fetch camera name suggestions:', error);
          return of([]);
        }),
      );
  }

  public getUsers(): Observable<FsLprAxonVIUser[]> {
    return this._httpClient
      .get<FsLprAxonVIUser[]>('/api/integrations/axon/axon_vi/users/', { observe: 'response' })
      .pipe(
        map((response) => {
          if (Array.isArray(response.body)) {
            return response.body.map((user) => new FsLprAxonVIUser(user));
          }
          return [];
        }),
        catchError((error) => {
          console.error('Failed to fetch users:', error);
          return of([]);
        }),
      );
  }

  private processGetAxonVIPlatesParams(params: Partial<IFsLprAxonVIParameters>): void {
    if (!params.includeFacets) {
      delete params.includeFacets;
    }

    if (!params.cameraVendors || !params.cameraVendors.length) {
      delete params.cameraVendors;
    }

    if (!params.cameraModels || !params.cameraModels.length) {
      delete params.cameraModels;
    }
  }
}
